import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import AssessmentTemplateHeader from '@app/src/components/Configurations/SustainabilityLibraryPages/Assessments/AssessmentTemplateHeader'
import AssessmentTemplateRow from '@app/src/components/Configurations/SustainabilityLibraryPages/Assessments/AssessmentTemplateRow'
import { WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS } from '@app/src/components/CreateNewRequestModal/WfStandardQuestionnaire'
import EmptyState from '@app/src/components/EmptyState'
import type { Option } from '@app/src/components/Form/Select/ControlledSelect'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import Table from '@app/src/components/Table'
import usePagination from '@app/src/hooks/pagination'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import SustainabilityLibraryCollection from '@app/src/pages/Configurations/SustainabilityLibraryPages/SustainabilityLibraryCollection'
import { SUSTAINABILITY_LIBRARY_PAGE_IDS } from '@app/src/pages/Configurations/SustainabilityLibraryScene'
import { SortOrder } from '@app/src/types/filter'
import type { AssessmentTemplate, Request } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import paths from '@app/src/wf-constants/paths'
import FormatListNumbered from '@mui/icons-material/FormatListNumbered'
import { Button, Stack, Typography } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import { useHistory } from 'react-router-dom'

interface FormData {
  templateIds: { id: number; title: string }
}

const Assessments: React.FC = () => {
  const { items, count, isError, isLoading } = useFetchCollectionWithPost<AssessmentTemplate>({
    endpoint: endpoints.assessmentTemplateCollection,
    key: FetchKey.AssessmentTemplateCollection,
    payload: {
      filter: [],
      include: [],
    },
  })

  const [page, pageSize, setPage, setPageSize] = usePagination()
  const confirm = useConfirm()
  const { control, handleSubmit } = useForm<FormData>()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const hasWriteAccess = useWriteAccess()

  return (
    <SustainabilityLibraryCollection
      actionButtons={[
        ...insertIf<ActionButton>(hasWriteAccess, {
          label: formatMessage({ id: 'resourceCollections.general.create' }),
          onClick: () =>
            confirm({
              title: formatMessage({ id: 'assessments.createAssessmentModalTitle' }),
              content: (
                <Stack spacing={2}>
                  <Select<{ title: string; id: number }, Request>
                    name={WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS.TEMPLATE_IDS}
                    fieldLabel={formatMessage({ id: 'assessments.selectQuestionnaire' })}
                    control={control}
                    required
                    enableAutoSelect
                    forceFetch
                    navigation={{
                      url: endpoints.worldfavorTemplates,
                      type: 'post',
                      postObject: {
                        include: [],
                        filter: [],
                        sort: { target: 'title', order: SortOrder.ASCENDING },
                      },
                    }}
                    findSelectedValue={(value, option): boolean => value?.id === option?.value?.id}
                    objectToOption={({ title, id }: Request): Option<{ title: string; id: number }> => ({
                      label: title,
                      value: { title, id },
                    })}
                  />
                  <Typography variant="caption">
                    {formatMessage({ id: 'assessments.createAssessmentModalDescription' })}
                  </Typography>
                </Stack>
              ),
            }).then(() => {
              handleSubmit(values => {
                history.push(
                  generatePath(paths.sustainabilityLibrary, {
                    configurationsPage: SUSTAINABILITY_LIBRARY_PAGE_IDS.Assessments,
                    configurationsSubPage: values.templateIds?.id ?? 0,
                  }),
                )
              })()
            }),
          variant: 'contained',
        }),
      ]}
      enablePadding={{
        top: false,
        right: true,
      }}
    >
      <Table<AssessmentTemplate>
        data={items}
        HeaderComponent={AssessmentTemplateHeader}
        RowComponent={AssessmentTemplateRow}
        count={count}
        isError={isError}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        emptyState={
          <EmptyState
            iconComponent={FormatListNumbered}
            title={formatMessage({ id: 'assessments.emptyStateTitle' })}
            description={formatMessage({ id: 'assessments.emptyStateDescription' })}
            children={<Button variant="outlined">{formatMessage({ id: 'resourceCollections.general.create' })}</Button>}
          />
        }
      />
    </SustainabilityLibraryCollection>
  )
}

export default Assessments
